import React, { useState, useEffect } from 'react';
import {
  ArrowsRightLeftIcon,
  ClockIcon,
  UserIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { ukSchedulingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ShiftSwaps = () => {
  const [swapRequests, setSwapRequests] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    my_shift_id: '',
    target_shift_id: '',
    target_employee_id: '',
    reason: '',
    notes: '',
    expires_at: ''
  });

  const { user, isAdmin, isManager, isEmployee } = useAuth();

  useEffect(() => {
    fetchSwapRequests();
    if (isEmployee()) {
      fetchMyShifts();
      fetchAvailableShifts();
    }
  }, []);

  const fetchSwapRequests = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getShiftSwaps({
        status: activeTab === 'all' ? undefined : activeTab
      });
      setSwapRequests(response.data.swap_requests || []);
    } catch (error) {
      console.error('Error fetching swap requests:', error);
      setError('Failed to load swap requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyShifts = async () => {
    try {
      const response = await ukSchedulingAPI.getMyShifts({
        start_date: new Date().toISOString().split('T')[0],
        include_future: true
      });
      setMyShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error fetching my shifts:', error);
    }
  };

  const fetchAvailableShifts = async () => {
    try {
      const response = await ukSchedulingAPI.getAvailableShifts({
        start_date: new Date().toISOString().split('T')[0],
        exclude_employee: user.id
      });
      setAvailableShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error fetching available shifts:', error);
    }
  };

  const handleCreateSwapRequest = async () => {
    try {
      setLoading(true);
      const swapData = {
        ...formData,
        requester_id: user.id,
        company_id: user.companyId,
        status: 'pending'
      };

      await ukSchedulingAPI.createShiftSwap(swapData);
      await fetchSwapRequests();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating swap request:', error);
      setError('Failed to create swap request');
    } finally {
      setLoading(false);
    }
  };

  const handleSwapAction = async (swapId, action, response_notes = '') => {
    try {
      setLoading(true);
      await ukSchedulingAPI.respondToShiftSwap(swapId, {
        action: action,
        response_notes: response_notes,
        responded_by: user.id
      });
      await fetchSwapRequests();
      setShowDetailsModal(false);
    } catch (error) {
      console.error(`Error ${action} swap request:`, error);
      setError(`Failed to ${action} swap request`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSwap = async (swapId) => {
    if (!window.confirm('Are you sure you want to cancel this swap request?')) return;

    try {
      setLoading(true);
      await ukSchedulingAPI.cancelShiftSwap(swapId);
      await fetchSwapRequests();
    } catch (error) {
      console.error('Error cancelling swap request:', error);
      setError('Failed to cancel swap request');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      my_shift_id: '',
      target_shift_id: '',
      target_employee_id: '',
      reason: '',
      notes: '',
      expires_at: ''
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
      expired: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Expired' }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatShiftTime = (startTime, endTime) => {
    return `${startTime?.substring(0, 5)} - ${endTime?.substring(0, 5)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const canRespondToSwap = (swap) => {
    if (swap.status !== 'pending') return false;
    if (swap.requester_id === user.id) return false;
    if (swap.target_employee_id && swap.target_employee_id !== user.id) return false;
    if (isManager() || isAdmin()) return true;
    return swap.target_shift?.assigned_employee_id === user.id;
  };

  const canCancelSwap = (swap) => {
    return swap.requester_id === user.id && swap.status === 'pending';
  };

  if (loading && swapRequests.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shift Swaps</h1>
          <p className="mt-1 text-sm text-gray-500">
            Request and manage shift exchanges with colleagues
          </p>
        </div>
        {isEmployee() && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Request Swap
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'pending', label: 'Pending', count: swapRequests.filter(s => s.status === 'pending').length },
            { key: 'approved', label: 'Approved', count: swapRequests.filter(s => s.status === 'approved').length },
            { key: 'rejected', label: 'Rejected', count: swapRequests.filter(s => s.status === 'rejected').length },
            { key: 'all', label: 'All', count: swapRequests.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setTimeout(() => fetchSwapRequests(), 100);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === tab.key
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Swap Requests List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {swapRequests.length === 0 ? (
          <div className="text-center py-12">
            <ArrowsRightLeftIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No swap requests</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isEmployee() ? 'Get started by creating a swap request.' : 'No swap requests found.'}
            </p>
            {isEmployee() && (
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary inline-flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Request Swap
                </button>
              </div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {swapRequests.map((swap) => (
              <li key={swap.id}>
                <div className="px-4 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <ArrowsRightLeftIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {swap.requester_name}
                          </p>
                          <ArrowsRightLeftIcon className="h-4 w-4 text-gray-400" />
                          <p className="text-sm text-gray-500 truncate">
                            {swap.target_shift?.assigned_employee_name || 'Open swap'}
                          </p>
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <CalendarDaysIcon className="h-4 w-4 mr-1" />
                            <span>{formatDate(swap.original_shift?.shift_date)}</span>
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            <span>{formatShiftTime(swap.original_shift?.start_time, swap.original_shift?.end_time)}</span>
                          </div>
                          <div className="capitalize">
                            {swap.reason}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(swap.status)}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedRequest(swap);
                            setShowDetailsModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                        >
                          <EyeIcon className="h-4 w-4 inline mr-1" />
                          View
                        </button>
                        
                        {canRespondToSwap(swap) && (
                          <>
                            <button
                              onClick={() => {
                                const notes = prompt('Response notes (optional):');
                                handleSwapAction(swap.id, 'approve', notes || '');
                              }}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              <CheckIcon className="h-4 w-4 inline mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                const notes = prompt('Response notes (optional):');
                                handleSwapAction(swap.id, 'reject', notes || '');
                              }}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              <XMarkIcon className="h-4 w-4 inline mr-1" />
                              Reject
                            </button>
                          </>
                        )}
                        
                        {canCancelSwap(swap) && (
                          <button
                            onClick={() => handleCancelSwap(swap.id)}
                            className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Request Shift Swap</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleCreateSwapRequest(); }}>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      My Shift to Swap *
                    </label>
                    <select
                      required
                      value={formData.my_shift_id}
                      onChange={(e) => setFormData({ ...formData, my_shift_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select your shift...</option>
                      {myShifts.map(shift => (
                        <option key={shift.id} value={shift.id}>
                          {formatDate(shift.shift_date)} - {formatShiftTime(shift.start_time, shift.end_time)} ({shift.position_name || shift.department_name})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Shift (Optional)
                    </label>
                    <select
                      value={formData.target_shift_id}
                      onChange={(e) => {
                        const targetShift = availableShifts.find(s => s.id === e.target.value);
                        setFormData({ 
                          ...formData, 
                          target_shift_id: e.target.value,
                          target_employee_id: targetShift?.assigned_employee_id || ''
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select target shift...</option>
                      {availableShifts.map(shift => (
                        <option key={shift.id} value={shift.id}>
                          {formatDate(shift.shift_date)} - {formatShiftTime(shift.start_time, shift.end_time)} 
                          {shift.assigned_employee_name && ` (${shift.assigned_employee_name})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Swap *
                  </label>
                  <select
                    required
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Select reason...</option>
                    <option value="personal">Personal Commitment</option>
                    <option value="medical">Medical Appointment</option>
                    <option value="family">Family Emergency</option>
                    <option value="education">Education/Training</option>
                    <option value="vacation">Planned Leave</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Expires
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Any additional information..."
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Request Swap'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Swap Request Details</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ArrowsRightLeftIcon className="h-6 w-6 text-gray-400" />
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">Shift Swap Request</h4>
                    <p className="text-sm text-gray-500">
                      Requested by {selectedRequest.requester_name}
                    </p>
                  </div>
                </div>
                {getStatusBadge(selectedRequest.status)}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="text-sm font-medium text-gray-900 mb-2">Request Details</h5>
                <div className="space-y-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Reason:</span> {selectedRequest.reason}
                  </div>
                  {selectedRequest.notes && (
                    <div>
                      <span className="font-medium">Notes:</span> {selectedRequest.notes}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Requested:</span> {formatDate(selectedRequest.created_at)}
                  </div>
                  {selectedRequest.expires_at && (
                    <div>
                      <span className="font-medium">Expires:</span> {formatDate(selectedRequest.expires_at)}
                    </div>
                  )}
                </div>
              </div>

              {selectedRequest.response_notes && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-yellow-900 mb-2">Response</h5>
                  <p className="text-sm text-yellow-700">{selectedRequest.response_notes}</p>
                  {selectedRequest.responded_by_name && (
                    <p className="text-xs text-yellow-600 mt-1">
                      Responded by {selectedRequest.responded_by_name} on {formatDate(selectedRequest.responded_at)}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
              {canCancelSwap(selectedRequest) && (
                <button
                  onClick={() => handleCancelSwap(selectedRequest.id)}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200"
                >
                  Cancel Request
                </button>
              )}
              
              {canRespondToSwap(selectedRequest) && (
                <>
                  <button
                    onClick={() => {
                      const notes = prompt('Response notes (optional):');
                      handleSwapAction(selectedRequest.id, 'reject', notes || '');
                    }}
                    className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      const notes = prompt('Response notes (optional):');
                      handleSwapAction(selectedRequest.id, 'approve', notes || '');
                    }}
                    className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200"
                  >
                    Approve
                  </button>
                </>
              )}
              
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftSwaps;